import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { IError } from '@axmit/error-helper';
import { ICirStoreCollection, ICirWeekCollection } from 'common/models/commonModel';
import { AppDispatch } from 'app/store';
import {
  IMonitorSalesCategorySalesModel,
  IMonitorSalesParams,
  IMonitorSalesOosOpportunityLossModel,
  IMonitorSalesProductRatioModel,
  IMonitorSalesProductSalesModel,
  IMonitorSalesSalesHeatmapModel,
  IMonitorSalesSummaryModel,
} from 'entities/MonitorSales/MonitorSales.models';
import { monitorSalesTransport } from 'entities/MonitorSales/MonitorSales.transport';

export interface IState {
  // sales summary
  monitorSalesSummaryModelLoading: boolean;
  monitorSalesSummaryModel: IMonitorSalesSummaryModel | null;
  monitorSalesSummaryModelError: IError | null;
  // product sales
  monitorSalesProductSalesModelLoading: boolean;
  monitorSalesProductSalesModel: IMonitorSalesProductSalesModel | null;
  monitorSalesProductSalesModelError: IError | null;
  // category sales
  monitorSalesCategorySalesModelLoading: boolean;
  monitorSalesCategorySalesModel: IMonitorSalesCategorySalesModel | null;
  monitorSalesCategorySalesModelError: IError | null;
  // oos opportunity loss
  monitorSalesOosModelLoading: boolean;
  monitorSalesOosModel: IMonitorSalesOosOpportunityLossModel | null;
  monitorSalesOosModelError: IError | null;
  // sales heatmap
  monitorSalesHeatmapModelLoading: boolean;
  monitorSalesHeatmapModel: IMonitorSalesSalesHeatmapModel | null;
  monitorSalesHeatmapModelError: IError | null;
  // product ratio
  monitorSalesProductRatioModelLoading: boolean;
  monitorSalesProductRatioModel: IMonitorSalesProductRatioModel | null;
  monitorSalesProductRatioModelError: IError | null;
  // stores
  monitorSalesStoresCollectionLoading: boolean;
  monitorSalesStoresCollection: ICirStoreCollection | null;
  monitorSalesStoresCollectionError: IError | null;
  // weeks
  monitorSalesWeeksCollectionLoading: boolean;
  monitorSalesWeeksCollection: ICirWeekCollection | null;
  monitorSalesWeeksCollectionError: IError | null;
}

const initialState: IState = {
  // sales summary
  monitorSalesSummaryModelLoading: false,
  monitorSalesSummaryModel: null,
  monitorSalesSummaryModelError: null,
  // product sales
  monitorSalesProductSalesModelLoading: false,
  monitorSalesProductSalesModel: null,
  monitorSalesProductSalesModelError: null,
  // category sales
  monitorSalesCategorySalesModelLoading: false,
  monitorSalesCategorySalesModel: null,
  monitorSalesCategorySalesModelError: null,
  // oos opportunity loss
  monitorSalesOosModelLoading: false,
  monitorSalesOosModel: null,
  monitorSalesOosModelError: null,
  // sales heatmap
  monitorSalesHeatmapModelLoading: false,
  monitorSalesHeatmapModel: null,
  monitorSalesHeatmapModelError: null,
  // product ratio
  monitorSalesProductRatioModelLoading: false,
  monitorSalesProductRatioModel: null,
  monitorSalesProductRatioModelError: null,
  // stores
  monitorSalesStoresCollectionLoading: false,
  monitorSalesStoresCollection: null,
  monitorSalesStoresCollectionError: null,
  // weeks
  monitorSalesWeeksCollectionLoading: false,
  monitorSalesWeeksCollection: null,
  monitorSalesWeeksCollectionError: null,
};

export const monitorSalesSlice = createSlice({
  name: 'monitorSales',
  initialState,
  reducers: {
    // sales summary
    setMonitorSalesSummaryModelLoading(state, action: PayloadAction<boolean>) {
      state.monitorSalesSummaryModelLoading = action.payload;
    },
    setMonitorSalesSummaryModel(state, action: PayloadAction<IMonitorSalesSummaryModel | null>) {
      state.monitorSalesSummaryModel = action.payload;
    },
    setMonitorSalesSummaryModelError(state, action: PayloadAction<IError | null>) {
      state.monitorSalesSummaryModelError = action.payload;
    },
    // product sales
    setMonitorSalesProductSalesModelLoading(state, action: PayloadAction<boolean>) {
      state.monitorSalesProductSalesModelLoading = action.payload;
    },
    setMonitorSalesProductSalesModel(state, action: PayloadAction<IMonitorSalesProductSalesModel | null>) {
      state.monitorSalesProductSalesModel = action.payload;
    },
    setMonitorSalesProductSalesModelError(state, action: PayloadAction<IError | null>) {
      state.monitorSalesProductSalesModelError = action.payload;
    },
    // category sales
    setMonitorSalesCategorySalesModelLoading(state, action: PayloadAction<boolean>) {
      state.monitorSalesCategorySalesModelLoading = action.payload;
    },
    setMonitorSalesCategorySalesModel(state, action: PayloadAction<IMonitorSalesCategorySalesModel | null>) {
      state.monitorSalesCategorySalesModel = action.payload;
    },
    setMonitorSalesCategorySalesModelError(state, action: PayloadAction<IError | null>) {
      state.monitorSalesCategorySalesModelError = action.payload;
    },
    // oos opportunity loss
    setMonitorSalesOosModelLoading(state, action: PayloadAction<boolean>) {
      state.monitorSalesOosModelLoading = action.payload;
    },
    setMonitorSalesOosModel(state, action: PayloadAction<IMonitorSalesOosOpportunityLossModel | null>) {
      state.monitorSalesOosModel = action.payload;
    },
    setMonitorSalesOosModelError(state, action: PayloadAction<IError | null>) {
      state.monitorSalesOosModelError = action.payload;
    },
    // sales heatmap
    setMonitorSalesHeatmapModelLoading(state, action: PayloadAction<boolean>) {
      state.monitorSalesHeatmapModelLoading = action.payload;
    },
    setMonitorSalesHeatmapModel(state, action: PayloadAction<IMonitorSalesSalesHeatmapModel | null>) {
      state.monitorSalesHeatmapModel = action.payload;
    },
    setMonitorSalesHeatmapModelError(state, action: PayloadAction<IError | null>) {
      state.monitorSalesHeatmapModelError = action.payload;
    },
    // product ratio
    setMonitorSalesProductRatioModelLoading(state, action: PayloadAction<boolean>) {
      state.monitorSalesProductRatioModelLoading = action.payload;
    },
    setMonitorSalesProductRatioModel(state, action: PayloadAction<IMonitorSalesProductRatioModel | null>) {
      state.monitorSalesProductRatioModel = action.payload;
    },
    setMonitorSalesProductRatioModelError(state, action: PayloadAction<IError | null>) {
      state.monitorSalesProductRatioModelError = action.payload;
    },
    // stores
    setMonitorSalesStoresCollectionLoading(state, action: PayloadAction<boolean>) {
      state.monitorSalesStoresCollectionLoading = action.payload;
    },
    setMonitorSalesStoresCollection(state, action: PayloadAction<ICirStoreCollection | null>) {
      state.monitorSalesStoresCollection = action.payload;
    },
    setMonitorSalesStoresCollectionError(state, action: PayloadAction<IError | null>) {
      state.monitorSalesStoresCollectionError = action.payload;
    },
    // weeks
    setMonitorSalesWeeksCollectionLoading(state, action: PayloadAction<boolean>) {
      state.monitorSalesWeeksCollectionLoading = action.payload;
    },
    setMonitorSalesWeeksCollection(state, action: PayloadAction<ICirWeekCollection | null>) {
      state.monitorSalesWeeksCollection = action.payload;
    },
    setMonitorSalesWeeksCollectionError(state, action: PayloadAction<IError | null>) {
      state.monitorSalesWeeksCollectionError = action.payload;
    },
  },
});

export const {
  // sales summary
  setMonitorSalesSummaryModelLoading,
  setMonitorSalesSummaryModel,
  setMonitorSalesSummaryModelError,
  // product sales
  setMonitorSalesProductSalesModelLoading,
  setMonitorSalesProductSalesModel,
  setMonitorSalesProductSalesModelError,
  // category sales
  setMonitorSalesCategorySalesModelLoading,
  setMonitorSalesCategorySalesModel,
  setMonitorSalesCategorySalesModelError,
  // oos opportunity loss
  setMonitorSalesOosModelLoading,
  setMonitorSalesOosModel,
  setMonitorSalesOosModelError,
  // sales heatmap
  setMonitorSalesHeatmapModelLoading,
  setMonitorSalesHeatmapModel,
  setMonitorSalesHeatmapModelError,
  // product ratio
  setMonitorSalesProductRatioModelLoading,
  setMonitorSalesProductRatioModel,
  setMonitorSalesProductRatioModelError,
  // stores
  setMonitorSalesStoresCollectionLoading,
  setMonitorSalesStoresCollection,
  setMonitorSalesStoresCollectionError,
  // weeks
  setMonitorSalesWeeksCollectionLoading,
  setMonitorSalesWeeksCollection,
  setMonitorSalesWeeksCollectionError,
} = monitorSalesSlice.actions;
export default monitorSalesSlice.reducer;

// Thunks

export const getMonitorSalesSummaryModel = (params?: IMonitorSalesParams) => {
  async function thunk(dispatch: AppDispatch) {
    dispatch(setMonitorSalesSummaryModelLoading(true));

    try {
      const data = await monitorSalesTransport.getSalesSummary(params);
      dispatch(setMonitorSalesSummaryModel(data));
    } catch (error) {
      dispatch(setMonitorSalesSummaryModelError(error as IError));
    } finally {
      dispatch(setMonitorSalesSummaryModelLoading(false));
    }
  }

  return thunk;
};

export const getMonitorSalesProductSalesModel = (params?: IMonitorSalesParams) => {
  async function thunk(dispatch: AppDispatch) {
    dispatch(setMonitorSalesProductSalesModelLoading(true));

    try {
      const data = await monitorSalesTransport.getProductSales(params);
      dispatch(setMonitorSalesProductSalesModel(data));
    } catch (error) {
      dispatch(setMonitorSalesProductSalesModelError(error as IError));
    } finally {
      dispatch(setMonitorSalesProductSalesModelLoading(false));
    }
  }

  return thunk;
};

export const getMonitorSalesCategorySalesModel = (params?: IMonitorSalesParams) => {
  async function thunk(dispatch: AppDispatch) {
    dispatch(setMonitorSalesCategorySalesModelLoading(true));

    try {
      const data = await monitorSalesTransport.getCategorySales(params);
      dispatch(setMonitorSalesCategorySalesModel(data));
    } catch (error) {
      dispatch(setMonitorSalesCategorySalesModelError(error as IError));
    } finally {
      dispatch(setMonitorSalesCategorySalesModelLoading(false));
    }
  }

  return thunk;
};

export const getMonitorSalesOosModel = (params?: IMonitorSalesParams) => {
  async function thunk(dispatch: AppDispatch) {
    dispatch(setMonitorSalesOosModelLoading(true));

    try {
      const data = await monitorSalesTransport.getOosOpportunityLoss(params);
      dispatch(setMonitorSalesOosModel(data));
    } catch (error) {
      dispatch(setMonitorSalesOosModelError(error as IError));
    } finally {
      dispatch(setMonitorSalesOosModelLoading(false));
    }
  }

  return thunk;
};

export const getMonitorSalesHeatmapModel = (params?: IMonitorSalesParams) => {
  async function thunk(dispatch: AppDispatch) {
    dispatch(setMonitorSalesHeatmapModelLoading(true));

    try {
      const data = await monitorSalesTransport.getSalesHeatmap(params);
      dispatch(setMonitorSalesHeatmapModel(data));
    } catch (error) {
      dispatch(setMonitorSalesHeatmapModelError(error as IError));
    } finally {
      dispatch(setMonitorSalesHeatmapModelLoading(false));
    }
  }

  return thunk;
};

export const getMonitorSalesProductRatioModel = (params?: IMonitorSalesParams) => {
  async function thunk(dispatch: AppDispatch) {
    dispatch(setMonitorSalesProductRatioModelLoading(true));

    try {
      const data = await monitorSalesTransport.getProductRatio(params);
      dispatch(setMonitorSalesProductRatioModel(data));
    } catch (error) {
      dispatch(setMonitorSalesProductRatioModelError(error as IError));
    } finally {
      dispatch(setMonitorSalesProductRatioModelLoading(false));
    }
  }

  return thunk;
};

export const getMonitorSalesStoresCollection = () => {
  async function thunk(dispatch: AppDispatch) {
    dispatch(setMonitorSalesStoresCollectionLoading(true));

    try {
      const data = await monitorSalesTransport.getStores();
      dispatch(setMonitorSalesStoresCollection(data));
    } catch (error) {
      dispatch(setMonitorSalesStoresCollectionError(error as IError));
    } finally {
      dispatch(setMonitorSalesStoresCollectionLoading(false));
    }
  }

  return thunk;
};

export const getMonitorSalesWeeksCollection = () => {
  async function thunk(dispatch: AppDispatch) {
    dispatch(setMonitorSalesWeeksCollectionLoading(true));

    try {
      const data = await monitorSalesTransport.getWeeks();
      dispatch(setMonitorSalesWeeksCollection(data));
    } catch (error) {
      dispatch(setMonitorSalesWeeksCollectionError(error as IError));
    } finally {
      dispatch(setMonitorSalesWeeksCollectionLoading(false));
    }
  }

  return thunk;
};
